// import React from 'react'
import { Auth0Provider } from '@auth0/auth0-react'
import type { PostHogConfig } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import App from './App.tsx'
import { AuthSync } from './components/auth-sync.tsx'
import { env } from './env.ts'
import './i18n'
import './index.css'
import { Providers } from './pages/end-users/ts-rest-provider.tsx'

// Error tracking and session replay stay off on developer machines, matching
// the previous Sentry setup: local runs should not create error issues or burn
// session-replay quota in the shared project. Product analytics is unaffected.
const isDev = import.meta.env.DEV

const options: Partial<PostHogConfig> = {
  api_host: env.VITE_PUBLIC_POSTHOG_HOST,
  // Autocapture unhandled errors for error tracking
  capture_exceptions: !isDev,
  // Web-vitals / performance capture
  capture_performance: !isDev,
  disable_session_recording: isDev,
  // Session replay with input masking on for privacy
  session_recording: {
    maskAllInputs: true,
  },
  // Tag every event with app context
  loaded: (posthog) => {
    posthog.register({
      app_name: 'alphabait-client',
      app_version: import.meta.env.VITE_PACKAGE_VERSION,
      environment: import.meta.env.MODE || 'production',
    })
  },
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  // <React.StrictMode>
  <BrowserRouter>
    <RecoilRoot>
      <Auth0Provider
        domain={env.VITE_AUTH0_DOMAIN}
        clientId={env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: env.VITE_AUTH0_REDIRECT_URL,
          scope: 'openid profile email',
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
      >
        <AuthSync />
        <Providers>
          <PostHogProvider
            apiKey={env.VITE_PUBLIC_POSTHOG_KEY ?? ''}
            options={options}
          >
            <App />
          </PostHogProvider>
        </Providers>
      </Auth0Provider>
    </RecoilRoot>
  </BrowserRouter>,
  // </React.StrictMode>,
)
